import { apiHelpers } from "../services/api";
import { showToast } from "./sweetAlert";

// Error types for consistent handling
export const ERROR_TYPES = {
  NETWORK: "NETWORK_ERROR",
  VALIDATION: "VALIDATION_ERROR",
  AUTH: "AUTH_ERROR",
  NOT_FOUND: "NOT_FOUND_ERROR",
  SERVER: "SERVER_ERROR",
  UNKNOWN: "UNKNOWN_ERROR",
};

// Get error type from error object
export const getErrorType = (error) => {
  if (apiHelpers.isNetworkError(error)) {
    return ERROR_TYPES.NETWORK;
  }

  if (apiHelpers.isValidationError(error)) {
    return ERROR_TYPES.VALIDATION;
  }

  if (apiHelpers.isAuthError(error)) {
    return ERROR_TYPES.AUTH;
  }

  if (error.status === 404) {
    return ERROR_TYPES.NOT_FOUND;
  }

  if (error.status >= 500) {
    return ERROR_TYPES.SERVER;
  }

  return ERROR_TYPES.UNKNOWN;
};

// Get user-friendly error message with vintage styling context
export const getErrorMessage = (error, context = "") => {
  const baseMessage = apiHelpers.getErrorMessage(error);
  const errorType = getErrorType(error);

  // Add context-specific messages
  const contextMessages = {
    login: "Unable to sign in to your account.",
    register: "Unable to create your account.",
    product: "Unable to load product secondaryrmation.",
    cart: "Unable to update your shopping cart.",
    checkout: "Unable to process your order.",
    profile: "Unable to update your profile.",
    orders: "Unable to load your order history.",
  };

  const contextMessage = contextMessages[context];

  switch (errorType) {
    case ERROR_TYPES.NETWORK:
      return contextMessage
        ? `${contextMessage} Please check your internet connection and try again.`
        : baseMessage;

    case ERROR_TYPES.AUTH:
      return contextMessage || "Please sign in to continue.";

    case ERROR_TYPES.VALIDATION:
      return baseMessage;

    case ERROR_TYPES.NOT_FOUND:
      return contextMessage
        ? `${contextMessage} The requested item could not be found.`
        : "The requested item could not be found.";

    case ERROR_TYPES.SERVER:
      return contextMessage
        ? `${contextMessage} Our servers are experiencing issues. Please try again later.`
        : "Our servers are experiencing issues. Please try again later.";

    default:
      return baseMessage;
  }
};

// Error logging utility
export const logError = (error, context = "", additionalInfo = {}) => {
  if (import.meta.env.DEV) {
    console.group(`🚨 Error in ${context || "Application"}`);
    console.error("Error:", error);
    console.error("Type:", getErrorType(error));
    console.error("Message:", getErrorMessage(error, context));

    if (Object.keys(additionalInfo).length > 0) {
      console.error("Additional Info:", additionalInfo);
    }

    if (error.stack) {
      console.error("Stack:", error.stack);
    }

    console.groupEnd();
  }

  // In production, you might want to send errors to a logging service
  // Example: sendToLoggingService(error, context, additionalInfo);
};

// Toast error notification utility
export const toastError = (error, context = "") => {
  const message = getErrorMessage(error, context);
  showToast(message, 'error');
};

// Retry utility with exponential backoff
export const withRetry = async (fn, maxRetries = 3, context = "") => {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry certain types of errors
      const errorType = getErrorType(error);
      if (
        errorType === ERROR_TYPES.AUTH ||
        errorType === ERROR_TYPES.VALIDATION
      ) {
        throw error;
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Log retry attempt
      if (import.meta.env.DEV) {
        console.warn(
          `Retry attempt ${attempt}/${maxRetries} for ${context}:`,
          error.message
        );
      }

      // Wait before retrying (exponential backoff)
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000); // Max 10 seconds
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // Log final failure
  logError(lastError, context, { attempts: maxRetries });
  throw lastError;
};

// Create error boundary helper for React components
export const createErrorBoundary = (fallbackComponent) => {
  return class ErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
      return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
      logError(error, "React Error Boundary", { errorInfo });
    }

    render() {
      if (this.state.hasError) {
        return fallbackComponent
          ? fallbackComponent(this.state.error)
          : React.createElement(
              "div",
              {
                className: "error-fallback p-8 text-center",
              },
              "Something went wrong. Please refresh the page."
            );
      }

      return this.props.children;
    }
  };
};
